import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#168aad] dark:focus-visible:ring-[#52b69a] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[#168aad] text-white hover:bg-[#1a759f] dark:hover:bg-[#1a759f]",
        destructive:
          "bg-[#ff453a] text-white hover:bg-[#ff3b30] dark:bg-[#ff453a] dark:hover:bg-[#ff3b30]",
        outline:
          "border border-[#d2d2d7] dark:border-[#424245] bg-transparent hover:bg-[#f5f5f7] dark:hover:bg-[#2d2d2f] text-[#1d1d1f] dark:text-[#f5f5f7]",
        secondary:
          "bg-[#f5f5f7] dark:bg-[#2d2d2f] text-[#1d1d1f] dark:text-[#f5f5f7] hover:bg-[#e5e5e7] dark:hover:bg-[#3d3d3f]",
        ghost: "text-[#1d1d1f] dark:text-[#f5f5f7] hover:text-[#168aad] dark:hover:text-[#52b69a] hover:bg-transparent",
        link: "text-[#168aad] dark:text-[#52b69a] hover:text-[#1a759f] dark:hover:text-[#76c893] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 rounded-full",
        sm: "h-8 rounded-full px-3 text-xs",
        lg: "h-11 rounded-full px-8",
        icon: "h-10 w-10 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
